import React, { memo, useCallback, useMemo } from 'react'

import {
  Button,
  Icon,
  BallModelBaseColor,
  BallModelClay,
  BallModelMetalness,
  BallModelNormals,
  BallModelPbr,
  BallModelRoughness,
  BallModelWireframe,
  CubeDraft,
}
from '@cgtrader/triangle-ui/dist/src/ssr-index'

import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useViewerStore } from 'Pods/ThreeDStudio/Hooks/useViewerStore'
import { useViewerContext } from 'Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext'

function RenderModeSelector() {
  const t = useTranslation()
  const viewer = useViewerContext()
  const renderMode = useViewerStore(viewer, "renderMode")
  const wireframe = useViewerStore(viewer, "wireframe")

  const buttons = useMemo(() => [
    {
      id: 'pbr',
      label: t('viewer.renderMode.pbr'),
      icon: BallModelPbr,
    },
    {
      id: 'clay',
      label: t('viewer.renderMode.clay'),
      icon: BallModelClay,
    },
    {
      id: 'basecolor',
      label: t('viewer.renderMode.baseColor'),
      icon: BallModelBaseColor,
    },
    {
      id: 'roughness',
      label: t('viewer.renderMode.roughness'),
      icon: BallModelRoughness,
    },
    {
      id: 'normals',
      label: t('viewer.renderMode.normals'),
      icon: BallModelNormals,
    },
    {
      id: 'metalness',
      label: t('viewer.renderMode.metalness'),
      icon: BallModelMetalness,
    },
    {
      id: 'segmentation',
      label: t('viewer.renderMode.segmentation'),
      icon: CubeDraft,
    },
  ], [t])

  const setRenderMode = useCallback(
    (mode) => {
      viewer?.setRenderModeSettings({ mode })
    },
    [viewer],
  )

  const updateWireFrameSettings = useCallback(
    (value) => {
      viewer?.setWireframeSettings(value)
    },
    [viewer],
  )

  if (!viewer || (!renderMode && !wireframe)) {
    return null
  }

  return (
    <div className="texture-selector">
      {renderMode && buttons
        // Segmentation is only offered when the opened model actually supports it
        // (the viewer adds it to availableModes once the mesh has segments).
        .filter(({ id }) => id !== 'segmentation' || renderMode.availableModes.has('segmentation'))
        .map(({ id, label, icon }) => (
        <DesktopOnlyTooltip content={label} key={id} id={`render-mode-${id}-tooltip`} place="bottom" >
          <Button
            type="ghost"
            variant="only-icon"
            size="tiny"
            icon={(
              <Icon
                svgComponent={icon}
                color={renderMode.mode === id ? 'primary' : 'darker'}
                notSpaced
              />
            )}
            isActive={renderMode.mode === id}
            disabled={!renderMode.availableModes.has(id)}
            onClick={() => setRenderMode(id)}
          />
        </DesktopOnlyTooltip>
      ))}
      {renderMode && wireframe && <div className="texture-selector__divider" />}
      {wireframe && (
        <DesktopOnlyTooltip
          content={t('viewer.renderMode.wireframe')}
          id="render-mode-wireframe-tooltip"
          place="bottom"
        >
          <Button
            type="ghost"
            variant="only-icon"
            size="tiny"
            isActive={wireframe.visible}
            onClick={() => updateWireFrameSettings({ visible: !wireframe.visible })}
            icon={(
              <Icon
                svgComponent={BallModelWireframe}
                color={wireframe.visible ? 'primary' : 'dark'}
                notSpaced
              />
            )}
          />
        </DesktopOnlyTooltip>
      )}
    </div>
  )
}

export default memo(RenderModeSelector)
